import {
  BarChart04,
  Calendar,
  Mail01,
  SearchLg,
  UserPlus01,
} from '@untitledui/icons';
import { useCallback } from 'react';

import type { IconComponentType } from '@/lib/base/badges/badge-types';
import { BadgeWithIcon } from '@/lib/base/badges/badges';

/**
 * A suggestion chip submits a starter prompt to the conversation; the agent
 * takes it from there. (In the landing-chat context "Draft email" also drops a
 * draft-email prompt into the chat rather than opening the standalone composer.)
 */
type Suggestion = {
  icon: IconComponentType;
  label: string;
  query: string;
};

const suggestions: Suggestion[] = [
  { icon: Mail01, label: 'Draft email', query: 'Draft an email' },
  {
    icon: Calendar,
    label: 'Create meeting',
    query: 'Create a meeting',
  },
  {
    icon: BarChart04,
    label: 'Build report',
    query: 'Build a report',
  },
  {
    icon: SearchLg,
    label: 'Research',
    query: 'Research a company or contact',
  },
  {
    icon: UserPlus01,
    label: 'Add contacts',
    query: 'Add new contacts',
  },
];

interface SuggestionChipProps {
  suggestion: Suggestion;
  onActivate: (suggestion: Suggestion) => void;
}

function SuggestionChip({
  suggestion,
  onActivate: handleActivate,
}: SuggestionChipProps) {
  const handleClick = useCallback(() => {
    handleActivate(suggestion);
  }, [handleActivate, suggestion]);

  return (
    <button
      type='button'
      onClick={handleClick}
      className='transition-transform hover:scale-105'
    >
      <BadgeWithIcon
        type='modern'
        size='md'
        iconLeading={suggestion.icon}
        className='cursor-pointer'
      >
        {suggestion.label}
      </BadgeWithIcon>
    </button>
  );
}

interface SuggestionBadgesProps {
  onSelect?: (text: string) => void;
}

export const SuggestionBadges = ({ onSelect }: SuggestionBadgesProps) => {
  const handleActivate = useCallback(
    (suggestion: Suggestion) => {
      onSelect?.(suggestion.query);
    },
    [onSelect]
  );

  return (
    <div className='flex flex-wrap items-center justify-center gap-2'>
      {suggestions.map(suggestion => (
        <SuggestionChip
          key={suggestion.label}
          suggestion={suggestion}
          onActivate={handleActivate}
        />
      ))}
    </div>
  );
};
